import { deleteProduct, getProductList } from '@/api';
import { productRelease, productUnrelease } from '@/api/devices';
import { ProductType, putProductImage, uploadImage } from '@/api/product';
import { AdvanceBreadcrumb } from '@/components/Breadcrumb';
import { useTableRequest } from '@/hooks';
import useAuthButton from '@/hooks/useAuthButton';
import { AppstoreOutlined, CameraOutlined, PlusOutlined, ReloadOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button, Card, Col, Empty, Form, Input, message, Pagination, Popconfirm, Radio, Row, Space, Spin, Switch, Table, Tag, Typography, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddModal, AddModalRef } from './AddModal';
import './ProductCard.less';


const { Text } = Typography;
const { useMessage } = message;

export interface ProductManageProps {}

// const NodeTypeMap: {[key: string]: string} = {
//   0: '未知类型',
//   1: '网关',
//   2: '直连设备',
//   3: '网管子设备',
// }

const ProductManage: React.FC<ProductManageProps> = () => {
  const [match] = useSearchParams();
  const [viewMode, setViewMode] = useState<'list' | 'card'>('card');
  const mountedRef = useRef(false);

  useEffect(() => {
    // 兼容旧的 create 参数
    if (match.has('create')) {
      addModalRef.current?.open();
      return;
    }

    // 处理新的 openAddModal 参数
    if (match.get('openAddModal') === 'true') {
      const category_template_id = match.get('category_template_id') || '';
      const category_template_name = match.get('category_template_name') || '';
      const name = match.get('name') || '';
      addModalRef.current?.open({
        category_template_id,
        category_template_name,
        name,
      });
    }
  }, [match]);

  const navigate = useNavigate();

  const addModalRef = useRef<AddModalRef>(null);

  const [messageApi, contextHolder] = useMessage();

  const { loading, form, dataSource, pagination, reload, search, reset } = useTableRequest<ProductType>({
    onRequest: getProductList,
    defaultPagination: { current: 1, pageSize: 12 },
  });

  // const { data: iotPlatformOptions } = useGetIotPlatform();
  // const { data: cloudInstanceOptions } = useGetCloudInstance();

  // useEffect(() => {
  //   setCloudInstanceId(cloudInstanceOptions[0]?.id);
  // }, [cloudInstanceOptions]);

  const { checkPermissionDisabled } = useAuthButton();

  

  // 根据视图模式切换每页显示数量
  useEffect(() => {
    // 页面首次加载时不执行
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
   
    if (viewMode === 'card') {
      pagination.onChange?.(pagination.current || 1, 12);
    } else if (viewMode === 'list') {
      pagination.onChange?.(pagination.current || 1, 10);
    }
  }, [viewMode]);

 

  // 处理图片上传
  const handleImageUpload = async (file: any, record: ProductType) => {
    try {
      // 上传图片
      const uploadResult = await uploadImage(file);

      if (uploadResult.success) {
        const updateResponse = await putProductImage(record.id, uploadResult?.result?.url);

        if (updateResponse.success) {
          messageApi.open({ type: 'success', content: '图片上传成功' });
          reload(); // 刷新列表
        } else {
          messageApi.open({ type: 'error', content: updateResponse.errorMsg || '更新失败' });
        }
      } else {
        messageApi.open({ type: 'error', content: uploadResult.errorMsg || '上传失败' });
      }
    } catch (error) {
      messageApi.open({ type: 'error', content: '上传失败，请重试' });
    }

    return false; // 阻止默认上传行为
  };

  return (
    <>
      {contextHolder}
      <AddModal ref={addModalRef} onFinish={search} />
      <AdvanceBreadcrumb title="产品管理" describe="在物联网平台中，某一类具有相同能力或特征的设备的合集被称为一款产品。" background={require('@/assets/images/product-background.png')} />
      <Card className="m20" size="small">
        <Form layout="inline" form={form}>
          <Form.Item key="name" name="name">
            <Input placeholder="产品名称"></Input>
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={search} disabled={checkPermissionDisabled('gateway:product:query')}>
              查询
            </Button>
          </Form.Item>
          <Form.Item>
            <Button type="default" onClick={reset}>
              重置
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <Card className="m20" size="small">
        <Space className="mb15 justify-space-between">
          <Button
            type="primary"
            onClick={() => {
              addModalRef.current?.open();
            }}
            disabled={checkPermissionDisabled('gateway:product:add')}
          >
            <PlusOutlined />
            创建产品
          </Button>
          <div className="flex-center">
            <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <Radio.Button value="card" style={{ lineHeight: '34px' }}>
                <AppstoreOutlined style={{ fontSize: '16px' }} />
              </Radio.Button>
              <Radio.Button value="list" style={{ lineHeight: '34px' }}>
                <UnorderedListOutlined style={{ fontSize: '16px' }} />
              </Radio.Button>
            </Radio.Group>
            <Button
              className="ml10"
              disabled={checkPermissionDisabled('gateway:product:query')}
              onClick={() => {
                reload();
              }}
            >
              <ReloadOutlined />
              刷新
            </Button>
          </div>
        </Space>
        {viewMode === 'list' ? (
          <Table
            loading={loading}
            dataSource={dataSource}
            pagination={pagination}
            bordered
            rowKey="id"
            size="small"
            scroll={{ x: 'max-content' }}
            columns={[
              { title: '产品ID', key: 'id', dataIndex: 'id', fixed: 'left' },
              { title: '产品名称', key: 'name', dataIndex: 'name', fixed: 'left' },
              {
                title: '产品编号',
                key: 'key',
                dataIndex: 'key',
                render(value) {
                  return value ? <Text copyable={{ tooltips: ['复制', '复制成功'] }}>{value}</Text> : null;
                },
                fixed: 'left',
              },
              { title: '接入协议驱动', key: 'drive_instance_name', dataIndex: 'drive_instance_name' },
              { title: '节点类型', key: 'node_type', dataIndex: 'node_type' },
              // { title: '云平台', key: 'platform', dataIndex: 'platform' },
              {
                title: '创建时间',
                key: 'created_at',
                dataIndex: 'created_at',
                render(value) {
                  return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
                },
              },
              {
                title: '发布状态',
                key: 'status',
                dataIndex: 'status',
                render: (value, record) => {
                  return (
                    <Popconfirm
                      title={value === '已发布' ? '是否确认取消发布？' : '是否确认发布？'}
                      okText="确认"
                      cancelText="取消"
                      disabled={checkPermissionDisabled('gateway:product:tsl:release')}
                      onConfirm={() => {
                        if (value === '已发布') {
                          productUnrelease(record.id).then((resp) => {
                            if (resp.success) {
                              messageApi.open({ type: 'success', content: '取消发布成功' });
                              reload();
                            } else {
                              messageApi.open({ type: 'error', content: resp.errorMsg });
                            }
                          });
                        } else {
                          productRelease(record.id).then((resp) => {
                            if (resp.success) {
                              messageApi.open({ type: 'success', content: '发布成功' });
                              reload();
                            } else {
                              messageApi.open({ type: 'error', content: resp.errorMsg });
                            }
                          });
                        }
                      }}
                    >
                      <Switch size="small" checked={value === '已发布'} disabled={checkPermissionDisabled('gateway:product:tsl:release')} />
                    </Popconfirm>
                  );
                },
              },
              {
                title: '操作',
                key: 'action',
                dataIndex: 'action',
                fixed: 'right',
                render(value, record) {
                  return (
                    <Space split={<Text type="secondary">/</Text>}>
                      <Typography.Link
                        onClick={() => {
                          navigate(`/gateway/product/detail/${record?.id}`);
                        }}
                      >
                        详情
                      </Typography.Link>
                      <Typography.Link
                        onClick={() => {
                          addModalRef.current?.open(record);
                        }}
                      >
                        编辑
                      </Typography.Link>
                      <Typography.Link
                        onClick={() => {
                          navigate(`/gateway/device/manage/${record?.id}`);
                        }}
                      >
                        管理设备
                      </Typography.Link>
                      <Popconfirm
                        title="此操作将永久删除该条记录, 是否继续?"
                        okText="确定"
                        cancelText="取消"
                        placement="topRight"
                        disabled={checkPermissionDisabled('gateway:product:remove')}
                        onConfirm={() => {
                          deleteProduct(record?.id).then((resp) => {
                            if (resp.success) {
                              messageApi.open({ type: 'success', content: '删除成功' });
                              reload();
                            } else {
                              messageApi.open({ type: 'error', content: resp.errorMsg });
                            }
                          });
                        }}
                      >
                        <Typography.Link disabled={checkPermissionDisabled('gateway:product:remove')}>删除</Typography.Link>
                      </Popconfirm>
                    </Space>
                  );
                },
              },
            ]}
          />
        ) : (
          <Spin spinning={loading}>
            {
              !dataSource?.length && <Empty description={false} />
            }
            <Row gutter={[16, 16]}>
              {dataSource.map((record: ProductType) => (
                <Col key={record.id} xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
                  <Card
                    hoverable
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}
                    styles={{ body: { padding: 0 } }}
                    onClick={() => {
                      navigate(`/gateway/product/detail/${record?.id}`);
                    }}
                    cover={
                      <div onClick={(e) => e.stopPropagation()}>
                        <ImgCrop showGrid aspect={3 / 2} quality={0.8} minZoom={0.5} showReset modalTitle="裁剪图片" modalOk="确定" modalCancel="取消" cropperProps={{ restrictPosition: false } as any}>
                          <Upload showUploadList={false} beforeUpload={(file) => handleImageUpload(file, record)} accept="image/*">
                            <div
                              style={{
                                width: '100%',
                                paddingBottom: '66.66%',
                                position: 'relative',
                                cursor: 'pointer',
                              }}
                            >
                              {/* 背景图片层 */}
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundImage: record.image_url ? `url("${record.image_url}")` : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  backgroundRepeat: 'no-repeat',
                                }}
                              />
                              {/* 上传提示覆盖层 */}
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'rgba(0, 0, 0, 0.3)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: 0,
                                  transition: 'opacity 0.3s',
                                  color: 'white',
                                  fontSize: '16px',
                                }}
                                className="upload-overlay"
                              >
                                <CameraOutlined style={{ marginRight: '8px' }} />
                                {record.image_url ? '更换图片' : '上传图片'}
                              </div>
                            </div>
                          </Upload>
                        </ImgCrop>
                      </div>
                    }
                  >
                    <div style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'nowrap', alignItems: 'center', marginBottom: '8px' }}>
                        {/* 产品名称 */}
                        <div
                          style={{
                            color: '#1a1a1a',
                            fontSize: '15px',
                            fontWeight: 600,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginRight: '12px',
                          }}
                        >
                          {record.name}
                        </div>

                        <div 
                          style={{ flexShrink: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Button
                            type="primary"
                            ghost
                            size="small"
                            style={{
                              borderRadius: '20px',
                              fontSize: '12px',
                              marginRight: '8px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              addModalRef.current?.open(record);
                            }}
                          >
                            编辑
                          </Button>
                          <Popconfirm
                            title="此操作将永久删除该条记录, 是否继续?"
                            okText="确定"
                            cancelText="取消"
                            placement="topRight"
                            disabled={checkPermissionDisabled('gateway:product:remove')}
                            onConfirm={(e) => {
                              e?.stopPropagation();
                              deleteProduct(record?.id).then((resp) => {
                                if (resp.success) {
                                  messageApi.open({ type: 'success', content: '删除成功' });
                                  reload();
                                } else {
                                  messageApi.open({ type: 'error', content: resp.errorMsg });
                                }
                              });
                            }}
                            onCancel={(e) => {
                              e?.stopPropagation();
                            }}
                          >
                            <Button
                              type="primary"
                              ghost
                              size="small"
                              disabled={checkPermissionDisabled('gateway:product:remove')}
                              style={{
                                borderRadius: '20px',
                                fontSize: '12px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        </div>
                      </div>

                      <div
                        style={{
                          marginBottom: '12px',
                        }}
                      >
                        <Tag bordered={false} color="geekblue">
                          {record.protocol}
                        </Tag>
                        <Tag bordered={false} color="geekblue">
                          {record.brand || '无品牌'}
                        </Tag>
                      </div>

                      {/* 详细信息 */}
                      <Row gutter={[0, 8]} style={{ marginBottom: '' }}>
                        <Col span={8}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '13px', color: '#90A4AE', marginBottom: '4px', borderRight: '1px solid #f0f0f0' }}>设备数量</div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{record.device_number || '0'}</div>
                          </div>
                        </Col>
                        <Col span={8}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '13px', color: '#90A4AE', marginBottom: '4px', borderRight: '1px solid #f0f0f0' }}>产品类型</div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{record.node_type || '-'}</div>
                          </div>
                        </Col>
                        <Col span={8}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '13px', color: '#90A4AE', marginBottom: '4px' }}>发布状态</div>
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <Popconfirm
                                title={record.status === '已发布' ? '是否确认取消发布？' : '是否确认发布？'}
                                okText="确认"
                                cancelText="取消"
                                disabled={checkPermissionDisabled('gateway:product:tsl:release')}
                                onConfirm={() => {
                                  if (record.status === '已发布') {
                                    productUnrelease(record.id).then((resp) => {
                                      if (resp.success) {
                                        messageApi.open({ type: 'success', content: '取消发布成功' });
                                        reload();
                                      } else {
                                        messageApi.open({ type: 'error', content: resp.errorMsg });
                                      }
                                    });
                                  } else {
                                    productRelease(record.id).then((resp) => {
                                      if (resp.success) {
                                        messageApi.open({ type: 'success', content: '发布成功' });
                                        reload();
                                      } else {
                                        messageApi.open({ type: 'error', content: resp.errorMsg });
                                      }
                                    });
                                  }
                                }}
                              >
                                <Switch size="small" checked={record.status === '已发布'} disabled={checkPermissionDisabled('gateway:product:tsl:release')} />
                              </Popconfirm>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
            {dataSource.length > 0 && (
              <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                  size="small"
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={pagination.onChange}
                  onShowSizeChange={pagination.onChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total) => `共 ${total} 条`}
                />
              </div>
            )}
          </Spin>
        )}
      </Card>
    </>
  );
};

export default ProductManage;
